import {
  createContext, useContext, useEffect, useState, type ReactNode,
} from "react";
import type { ShopConfig, Breed } from "../lib/types";
import { subscribeShopConfig, subscribeClosedDates, defaultShopConfig } from "../data/config";
import { subscribeBreeds } from "../data/breeds";
import { applyPalette } from "../lib/palette";

interface ShopData {
  config: ShopConfig;
  breeds: Breed[];
  closedDates: string[];
  loading: boolean;
  error: string | null;
}

const ShopContext = createContext<ShopData | null>(null);

export function ShopProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<ShopConfig>(defaultShopConfig);
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [closedDates, setClosedDates] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState({ config: false, breeds: false, closed: false });

  useEffect(() => {
    const fail = (what: string) => (e: Error) => {
      console.error(`Failed to load ${what}:`, e);
      setError("We could not load the shop's settings. Please refresh, or try again in a moment.");
    };
    const unsubConfig = subscribeShopConfig((c) => {
      setConfig(c);
      setReady((r) => ({ ...r, config: true }));
    }, fail("config"));
    const unsubBreeds = subscribeBreeds((b) => {
      setBreeds(b);
      setReady((r) => ({ ...r, breeds: true }));
    }, fail("breeds"));
    const unsubClosed = subscribeClosedDates((d) => {
      setClosedDates(d);
      setReady((r) => ({ ...r, closed: true }));
    }, fail("closed dates"));
    return () => {
      unsubConfig();
      unsubBreeds();
      unsubClosed();
    };
  }, []);

  // Apply the stored palette to :root whenever it changes, so both customer and
  // admin pages theme live off config.
  useEffect(() => {
    applyPalette(config.palette);
  }, [config.palette]);

  const loading = !(ready.config && ready.breeds && ready.closed) && !error;

  return (
    <ShopContext.Provider value={{ config, breeds, closedDates, loading, error }}>
      {children}
    </ShopContext.Provider>
  );
}

export function useShop(): ShopData {
  const ctx = useContext(ShopContext);
  if (!ctx) throw new Error("useShop must be used within ShopProvider");
  return ctx;
}
